import { useEffect } from 'react'
import * as Sentry from '@sentry/gatsby'

const sendSentryError = (error: any) => {
  if (typeof Sentry === 'undefined') {
    console.error('Sentry is not defined')

    return
  }

  Sentry.captureException(error, {
    tags: {
      ssr: 'false',
      feature: 'swap-images-ab-test',
    },
    extra: {
      message: error?.message || 'Unknown error',
      stack: error?.stack ?? 'No stack trace',
    },
  })
}

export function useSwapImagesABTest() {
  useEffect(() => {
    if (!window.dynamicYieldUtils) {
      window.dynamicYieldUtils = {}
    }

    window.dynamicYieldUtils.swapImagesABTest = function swapImagesABTest(
      imageMap: Record<string, string>
    ) {
      try {
        // eslint-disable-next-line no-console
        console.log(
          '[DY Test] 🚀 Iniciando Teste AB de Imagens - V6 Estabilizada'
        )

        if (window.dynamicYieldUtils._swapImagesABTestObserver) {
          try {
            window.dynamicYieldUtils._swapImagesABTestObserver.disconnect()
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('[DY Test] Failed to disconnect observer:', e)
          }
        }

        const imagesToProcess = new Set<Element>()
        let isProcessing = false

        const swapImage = (element: Element) => {
          const attributesToCheck = ['src', 'srcset', 'data-src', 'data-srcset']
          let hasChanges = false

          attributesToCheck.forEach((attr) => {
            let currentValue = element.getAttribute(attr)

            if (!currentValue) return

            let modified = false

            for (const oldId in imageMap) {
              if (currentValue.includes(oldId)) {
                const newId = imageMap[oldId]

                const searchNormal = `/ids/${oldId}`
                const replaceNormal = `/ids/${newId}`

                const searchEncoded = `%2Fids%2F${oldId}`
                const replaceEncoded = `%2Fids%2F${newId}`

                // Substituição literal para não quebrar no interpretador da DY
                if (currentValue.includes(searchNormal)) {
                  currentValue = currentValue
                    .split(searchNormal)
                    .join(replaceNormal)
                  modified = true
                }

                if (currentValue.includes(searchEncoded)) {
                  currentValue = currentValue
                    .split(searchEncoded)
                    .join(replaceEncoded)
                  modified = true
                }
              }
            }

            if (modified) {
              element.setAttribute(attr, currentValue)
              hasChanges = true
            }
          })

          // Contorna o Cabo de Guerra de renderização com a VTEX
          if (hasChanges && element.tagName === 'IMG') {
            element.classList.remove(
              'lazyload',
              'vtex-render-runtime-8-x-lazyload',
              'lazyloading'
            )
            element.classList.add('lazyloaded')
          }
        }

        const processBatch = () => {
          imagesToProcess.forEach(swapImage)
          imagesToProcess.clear()
          isProcessing = false
        }

        const queueImage = (element: Element) => {
          imagesToProcess.add(element)
          if (!isProcessing) {
            isProcessing = true
            window.requestAnimationFrame(processBatch)
          }
        }

        const initialElements = document.querySelectorAll('img, source')

        initialElements.forEach(queueImage)

        // Cria e armazena o novo observer
        const observer = new MutationObserver((mutations) => {
          for (let i = 0; i < mutations.length; i++) {
            const mutation = mutations[i]

            if (mutation.type === 'childList') {
              for (let j = 0; j < mutation.addedNodes.length; j++) {
                const node = mutation.addedNodes[j] as Element

                if (node.nodeType === 1) {
                  if (node.tagName === 'IMG' || node.tagName === 'SOURCE') {
                    queueImage(node)
                  } else {
                    const elements = node.querySelectorAll('img, source')

                    elements.forEach(queueImage)
                  }
                }
              }
            } else if (
              mutation.type === 'attributes' &&
              ((mutation.target as Element).tagName === 'IMG' ||
                (mutation.target as Element).tagName === 'SOURCE')
            ) {
              queueImage(mutation.target as Element)
            }
          }
        })

        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['src', 'srcset', 'data-src', 'data-srcset'],
        })
        window.dynamicYieldUtils._swapImagesABTestObserver = observer
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[DY Test] Error in swapImagesABTest:', error)
        sendSentryError(error)
      }
    }

    return () => {
      delete window.dynamicYieldUtils.swapImagesABTest
    }
  }, [])
}
